import { expressQuerySelector, expressQuerySelectorAll, getParentWithClassName } from "../common/html";
import { initShowMoreElements } from "./show-more-elements";
import { createPopup } from "./popup-new";
import { getParameter, queryremovekeys, updateParameter } from "../utils/querystring";
import { initColorSwap } from "./color-swap";
import { initVideoPlayer } from "./video-player";
import { createNewsletterSubscribe, createNewsletterUnsubscribe } from "./newsletter";
import { createFilters } from "./filter-helper";

export function createProductGrid(grid: HTMLElement) {
	const gridCounter = parseInt(grid.dataset['gridcounter'] || '', 10);
	const filteredValues: { [gridCount: string]: { [filterCategory: string]: string[] } } = {};
	const regExpForNumberInParantheses = /\(([^)]+)\)/;
	const calculateHackFor2TilesOnLastLine = (grid: HTMLElement): void => {
		const filters = expressQuerySelector(grid.parentElement, '.technical-filters-container', false);
		const tiles = expressQuerySelectorAll(grid, '.technical-tile:not(.u-hide-on-desktop-full):not(.u-hide)');

		if (tiles.length % (filters ? 3 : 4) == 2)
			grid.classList.add('m-product-grid--tail-2-tiles-on-desktop');
		else
			grid.classList.remove('m-product-grid--tail-2-tiles-on-desktop');
	};
	const filterAndSortGrid = (grid: HTMLElement, filterName: string = null, sortMethod: string = null, selectedFilters: { [inputName: string]: string[] } = null): void => {
		const gridCounter = parseInt(grid.dataset['gridcounter'] || '', 10);
		const gridContent = expressQuerySelector(grid, '.technical-product-grid-content', true);
		const sortingGrid = expressQuerySelector(grid, '.technical-sorting-container', false);
		const tiles = expressQuerySelectorAll<HTMLElement>(grid, '.technical-tile');

		let reInitShowMore = true;
		const resetTiles = filterName === 'reset' || (filteredValues[gridCounter] && (Object.keys(filteredValues[gridCounter]).every(x => !filteredValues[gridCounter][x] || filteredValues[gridCounter][x].length === 0) || Object.keys(filteredValues[gridCounter]).length === 0));

		// Reset all tiles
		tiles.forEach(tile => {
			tile.classList.remove("technical-showmore");
			tile.classList.remove("u-hide-on-desktop-full");
			tile.classList.remove("u-hide-on-mobile-full");
			if (resetTiles) tile.classList.remove('u-hide');
		});

		// Reset the show more
		const button = expressQuerySelector(grid, '.technical-showmore-button', false);
		button && button.classList.remove('u-hide');

		// Sort the tiles
		if (filterName === 'reset') {
			const desktopSortingContainer = expressQuerySelector<HTMLSelectElement>(grid, '.technical-sorting-container .technical-dropdown', false);
			const mobileSortingContainer = expressQuerySelector<HTMLInputElement>(document, `#mobile${gridCounter}-default`, false);

			if (desktopSortingContainer) desktopSortingContainer.value = 'default';
			if (mobileSortingContainer) mobileSortingContainer.checked = true;
			sortMethod = 'default';
		}
		if (sortMethod) {
			tiles.sort((a, b) => { // This sort method will sort by the actual sortmethod BUT (see next comment)
				switch (sortMethod) {
					case "price-low-high":
						if (parseFloat(a.dataset.sortPrice) === parseFloat(b.dataset.sortPrice))
							return parseInt(a.dataset.sortDefault, 10) > parseInt(b.dataset.sortDefault, 10) ? 1 : -1;
						return parseFloat(a.dataset.sortPrice) > parseFloat(b.dataset.sortPrice) ? 1 : -1;
					case "price-high-low":
						if (parseFloat(a.dataset.sortPrice) === parseFloat(b.dataset.sortPrice))
							return parseInt(a.dataset.sortDefault, 10) > parseInt(b.dataset.sortDefault, 10) ? 1 : -1;
						return parseFloat(a.dataset.sortPrice) <= parseFloat(b.dataset.sortPrice) ? 1 : -1;
					case 'delivery-early-late':
						if (a.dataset.sortDate === b.dataset.sortDate)
							return parseInt(a.dataset.sortDefault, 10) > parseInt(b.dataset.sortDefault, 10) ? 1 : -1;
						return a.dataset.sortDate > b.dataset.sortDate ? 1 : -1;
					default:
						return parseInt(a.dataset.sortDefault, 10) > parseInt(b.dataset.sortDefault, 10) ? 1 : -1;
				}
			});

			// This will put back the immutable tiles (e.g. tiles with no price)
			if (sortMethod !== 'default') {
				let wrongTileIndex = 999;
				while (wrongTileIndex >= 0) {
					wrongTileIndex = tiles.findIndex((value, index) => {
						const defaultIndex = parseInt(value.dataset.sortDefault, 10);
						if (defaultIndex === index)
							return false;

						if (sortMethod == 'delivery-early-late' && !value.dataset.sortDate)
							return true;
						else if (parseFloat(value.dataset.sortPrice) == -1)
							return true;
					});

					if (wrongTileIndex >= 0) {
						const element = tiles[wrongTileIndex];
						tiles.splice(wrongTileIndex, 1);
						tiles.splice(parseInt(element.dataset.sortDefault, 10), 0, element);
					}
				}
			}

			// This will put back the tiles in the DOM
			tiles.forEach(node => {
				const delivery = expressQuerySelector(node, '.technical-deliverydate', false);
				if (delivery) {
					if (sortMethod == 'delivery-early-late')
						delivery.classList.remove('u-hide');
					else
						delivery.classList.add('u-hide');
				}
				gridContent.appendChild(node);
			});
		}

		// Only show tiles that have the specific filter value
		if (filteredValues[gridCounter] && Object.keys(filteredValues[gridCounter]).length > 0 && Object.keys(filteredValues[gridCounter]).some(x => filteredValues[gridCounter][x] && filteredValues[gridCounter][x].length > 0)) {

			// Hide the show more button (don't need it)
			const button = expressQuerySelector(grid, '.technical-showmore-button', false);
			if (button)
				button.classList.add('u-hide');

			tiles.forEach(tile => {
				if (!selectedFilters) return; // no filters => nothing to do
				const tags = tile.dataset.tagid.split('|');
				let validtile = true;

				Object.keys(selectedFilters).forEach(category => {
					const selectedFilterInCategory = selectedFilters[category];

					validtile = validtile && (!selectedFilterInCategory.length || tags.filter(x => selectedFilterInCategory.includes(x)).length > 0);
				});

				tile.classList.toggle("u-hide", !validtile);
			});

			reInitShowMore = false;
		}

		// all tiles hidden => show no elements label
		const noElementsEl = expressQuerySelector(gridContent, '.technical-no-elements', false);
		noElementsEl && noElementsEl.classList.toggle('u-hide', !tiles.every(t => t.classList.contains('u-hide')));
		if (sortingGrid) {
			const sortEl = expressQuerySelector(sortingGrid, '.technical-dropdown', false);
			if (sortEl) {
				sortingGrid.classList.toggle('a-form-input--disabled', tiles.every(t => t.classList.contains('u-hide')));
				sortEl.toggleAttribute('disabled', tiles.every(t => t.classList.contains('u-hide')));
			}
		}

		if (reInitShowMore) {
			const showMore = expressQuerySelector<HTMLElement>(grid, '.technical-showmore-button', true);

			if (resetTiles) {
				showMore.dataset.currentpage = '0';
				window.history.pushState({}, null, queryremovekeys(['currentpage[' + gridCounter + ']']));
			}

			const baseAmountDesktop = parseInt(showMore.dataset.baseamountDesktop, 10);
			const baseAmountMobile = parseInt(showMore.dataset.baseamountMobile, 10);
			const batchsizeDesktop = parseInt(showMore.dataset.batchsizeDesktop, 10);
			const batchsizeMobile = parseInt(showMore.dataset.batchsizeMobile, 10);

			const currentPage = parseInt(getParameter('currentpage[' + gridCounter + ']') || '0', 10);
			let hideButtonDesktop = true;
			let hideButtonMobile = true;
			for (let i = 0; i < tiles.length; i++) {
				if (i >= baseAmountDesktop + (currentPage * batchsizeDesktop)) {
					tiles[i].classList.add('u-hide-on-desktop-full');
					tiles[i].classList.add('technical-showmore');
					hideButtonDesktop = false;
				}
				if (i >= baseAmountMobile + (currentPage * batchsizeMobile)) {
					tiles[i].classList.add('u-hide-on-mobile-full');
					tiles[i].classList.add('technical-showmore');
					hideButtonMobile = false;
				}
			}

			showMore.parentElement.classList.toggle('u-hide-on-desktop-full', hideButtonDesktop);
			showMore.parentElement.classList.toggle('u-hide-on-mobile-full', hideButtonMobile);

			if (window.showMores[gridCounter])
				window.showMores[gridCounter].dispose();
			window.showMores[gridCounter] = initShowMoreElements(grid, showMore, 'currentpage', null, gridCounter);
		}

		// Recount all the filter values except for the filter that was just clicked
		if (filterName) {
			const filters = expressQuerySelectorAll<HTMLElement>(grid.parentElement, '.technical-filter');
			filters.forEach(filter => {
				const filterValues = expressQuerySelectorAll<HTMLElement>(filter, '.technical-filter-value');
				const ownCategory = filter.dataset.filtercategory;
				filterValues.forEach(filterValue => {
					const label = expressQuerySelector<HTMLElement>(filterValue, 'label', true);
					const labelText = expressQuerySelector<HTMLElement>(label, '.technical-filter-value-label', true);
					const input = expressQuerySelector<HTMLInputElement>(filterValue, 'input', true);

					if (filterName === ownCategory) {
						const shouldFilterBeDisabled = !input.checked && (labelText.innerHTML.includes('(0)'));
						filterValue.classList.toggle('a-form-input--disabled', shouldFilterBeDisabled);
						input.toggleAttribute('disabled', shouldFilterBeDisabled);
						return;
					}

					const ownValue = filterValue.dataset.filterkey;
					const filtersToCheck = { ...selectedFilters };
					filtersToCheck[ownCategory] = !filtersToCheck[ownCategory] || filtersToCheck[ownCategory].length <= 0
						? [ownValue]
						: [...filtersToCheck[ownCategory], ownValue];
					const filterKeys = Object.keys(filtersToCheck);

					// if only filter selected => default valid tiles count
					const validTiles = filterValue.dataset.default_tile_count && filterKeys.every(x => (x != ownCategory && filtersToCheck[x].length == 0) || (x == ownCategory && filtersToCheck[x].length > 0))
						? parseInt(filterValue.dataset.default_tile_count)
						: tiles.filter(tile => {
							return tile.classList.contains('u-hide') // u-hide is toggled on in filter step above
								? false
								: tile.dataset.tagid.split('|').includes(ownValue);
						}).length;

					const shouldFilterBeDisabled = validTiles == 0 && !input.checked;
					filterValue.classList.toggle('a-form-input--disabled', shouldFilterBeDisabled);
					input.toggleAttribute('disabled', shouldFilterBeDisabled);

					const html = labelText.innerHTML;
					labelText.innerHTML = html.replace(regExpForNumberInParantheses, "(" + validTiles + ")");
				});
			});
		}

		calculateHackFor2TilesOnLastLine(grid);

		if (sortMethod) {
			let url = location.href;
			if (sortMethod === 'default')
				url = updateParameter('sortmethod[' + gridCounter + ']', '', url);
			else
				url = updateParameter('sortmethod[' + gridCounter + ']', sortMethod, url);
			if (url != location.href)
				history.replaceState(null, '', url.toLowerCase());
		}

		// update tile counter
		const gridAmount = expressQuerySelector<HTMLElement>(grid, '.technical-amount-of-tiles', false);
		if (!gridAmount) return; // when html is wrong in cache this element is not there
		const visibleTiles = tiles.filter(x => !x.classList.contains('u-hide')).length;
		gridAmount.innerHTML = visibleTiles > 1
			? (gridAmount.dataset.multi || '').replace('{0}', visibleTiles.toString())
			: visibleTiles <= 0 ? '' : (gridAmount.dataset.single || '');
		gridAmount.classList.toggle('u-hide', visibleTiles <= 0);
	};
	const filterCreateForOneGrid = (filterContainer: HTMLElement): { resetFiltersContainerEl: () => void } => {
		return createFilters(filterContainer,
			(filterName: string, filterValue: string, checked: boolean, containerEl: HTMLElement) => {
				const gridToFilter = expressQuerySelector<HTMLElement>(getParentWithClassName(containerEl, 'technical-grid-and-filters'), '.technical-product-grid', true);
				const gridCounter = parseInt(gridToFilter.dataset['gridcounter'] || '', 10);
				if (!filteredValues[gridCounter]) filteredValues[gridCounter] = {};
				filteredValues[gridCounter][filterName] = !filteredValues[gridCounter][filterName] || filteredValues[gridCounter][filterName].length <= 0
					? [filterValue]
					: checked ? [...filteredValues[gridCounter][filterName], filterValue] : filteredValues[gridCounter][filterName].filter(x => x != filterValue);

				filterAndSortGrid(gridToFilter, filterName, null, filteredValues[gridCounter]);
			},
			(containerEl: HTMLElement) => {
				// This resets the filter
				const gridToFilter = expressQuerySelector<HTMLElement>(getParentWithClassName(containerEl, 'technical-grid-and-filters'), '.technical-product-grid', true);
				const gridCounter = parseInt(gridToFilter.dataset['gridcounter'] || '', 10);
				filteredValues[gridCounter] = {};
				filterAndSortGrid(gridToFilter, 'reset', null, filteredValues[gridCounter]);
			}
		);
	};

	if (!grid.classList.contains('technical-load-api')) {
		const buttonEl = expressQuerySelector<HTMLElement>(grid.parentElement ? grid.parentElement : document, '.technical-showmore-button', false);
		if (buttonEl)
			window.showMores[gridCounter] = initShowMoreElements(grid, buttonEl, 'currentpage', null, gridCounter);

		// filter?
		const filterContainer = expressQuerySelector<HTMLElement>(grid.parentElement ? grid.parentElement : document, '.technical-filters-container', false);
		if (filterContainer) {
			// Desktop
			const desktopFilter = filterCreateForOneGrid(filterContainer);

			// Mobile
			const mobileButton = expressQuerySelector<HTMLElement>(grid, '.technical-filters-mobile-button', true);
			const popup = expressQuerySelector<HTMLElement>(document, '#MobileFilters' + gridCounter, true);
			const mobilecontainer = expressQuerySelector<HTMLElement>(popup, '.technical-filters-container', true);
			const mobileFilter = filterCreateForOneGrid(mobilecontainer);

			const onClickMobile = () => {
				createPopup({
					dialogId: 'MobileFilters' + gridCounter,
					view: 'none'
				}).then(popup => {
					if (!popup.popup) return;
					popup.openAsync();
					const button = expressQuerySelector(popup.popup, '.technical-close-dialog', true);
					button.addEventListener('click', popup.close);
				});
			};
			mobileButton.addEventListener('click', onClickMobile);

			const noElementsResetButton = expressQuerySelector(grid, '.technical-reset-filters', false);
			noElementsResetButton && noElementsResetButton.addEventListener('click', _ => {
				desktopFilter.resetFiltersContainerEl();
				mobileFilter.resetFiltersContainerEl();
			});

			// Mobile sorting
			const sortElsMobile = expressQuerySelectorAll<HTMLInputElement>(mobilecontainer, '.technical-sorting-radio');
			sortElsMobile.forEach(sortElMobile => {
				sortElMobile.addEventListener('click', () => {
					filterAndSortGrid(grid, null, sortElMobile.value);
				});
			});

		}

		// sorting?
		const sortEl = expressQuerySelector<HTMLSelectElement>(grid, '.technical-dropdown', false);
		if (sortEl) {
			sortEl.addEventListener('change', () => {
				const selectedSortingValue = sortEl[sortEl.selectedIndex] as HTMLOptionElement;
				filterAndSortGrid(grid, null, selectedSortingValue.value);
			});
		}

		calculateHackFor2TilesOnLastLine(grid);

		return;
	}
}
